//-- Copyright (c) 2021 TMT Insights Group, LLC
import { ChangeEventHandler, KeyboardEventHandler, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import styled from '@emotion/styled';
import { Button, Grid, TextField } from '@mui/material';
import { ConfigContext } from '@/contextAPI/configContext';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { clearSearchResults, searchTitles, clearInventoryResults, setToggledTitleSearchMode } from '@/redux/slices/orderPlacementSlice';
import { selectSelectedHierarchy, selectTitleSearchIsLoading, selectTitleSearchResults } from '@/redux/selectors/orderPlacementSelectors';
import { getTitleDetails, setActiveTitle, setFilteredChildId } from '@/redux/slices/titleSlice';
import { clearCache, fetchTitleChildrenV2, fetchTitleV2, getTitleVersions, setActiveTitleId, setRootTitleId } from '@/redux/slices/titleV2Slice';
import { useToast } from '@/components/_common/Toast';
import { convertStylesToStyled } from '@/_utils/utils';
import useDebounce from '@/components/_hooks/useDebounce';
import Spinner from '@/components/_common/Spinner';
import PolarisIcon from '@/components/_common/PolarisIcon';
import OPTitleSearchResults from '@/components/OrderPlacement/OPTitleSearch/OPTitleSearchResults';
import { selectActiveModuleTitleHistory } from '@/redux/selectors/authSelectors';
import { selectActiveTitleId } from '@/redux/selectors/titleSelectors';
import { UserPreferences } from '@/redux/slices/slice_types';
import { useConfigValue } from '@/components/_hooks/useConfigValue';
import { OrderPlacementConfig, TitleSearchMode } from '@/__configs/config_types';

type OPTitleSearchProps = {
  showBulkSearchButton?: boolean;
  alternateEntityTypesFilter?: string[];
};

const OPTitleSearch = ({ showBulkSearchButton, alternateEntityTypesFilter }: OPTitleSearchProps) => {
  const bulkTitleSearchOptions = useConfigValue<OrderPlacementConfig['bulkTitleSearchOptions']>(['orderPlacementConfig', 'bulkTitleSearchOptions']);
  const allowCsvOrPaste = !!bulkTitleSearchOptions?.size && !!showBulkSearchButton;
  const titleSort = useContext(ConfigContext)?.orderPlacementConfig?.titleSort || {};
  const isTitleV2 = useContext(ConfigContext)?.titleConfig?.v2;
  const dispatch = useAppDispatch();
  const isSearching = useAppSelector(selectTitleSearchIsLoading);
  const titleHistory = useAppSelector(selectActiveModuleTitleHistory);
  const activeTitleId = useAppSelector(selectActiveTitleId);
  const activeHierarchy = useAppSelector(selectSelectedHierarchy);
  const { error: errorToast } = useToast();
  const displayReadyTitleHistory = useMemo(() => {
    const filtered = activeTitleId ? titleHistory.filter((title) => title.id !== activeTitleId) : titleHistory;

    return [...filtered].reverse();
  }, [titleHistory, activeTitleId]);
  const results = useAppSelector(selectTitleSearchResults);
  const [searchValue, setSearchValue] = useState('');
  const [focused, setFocus] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState<null | number>(null);
  const debouncedSearchValue = useDebounce(searchValue, 500);
  const resultsRef = useRef<any>(null);
  const textFieldRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setShowResults(focused && searchValue.length > 0);
  }, [focused, searchValue]);

  useEffect(() => {
    setShowResults(false);
  }, [searchValue]);

  useEffect(() => {
    if (!debouncedSearchValue) {
      dispatch(clearSearchResults(undefined));
      return;
    }

    const promise = dispatch(searchTitles({ search: searchValue, ...titleSort, hierarchy_filter: activeHierarchy || undefined, entity_types_filter: alternateEntityTypesFilter }));

    (async (action) => {
      await action.unwrap();
      setShowResults(true);
    })(promise);

    return () => (promise as any).abort();
  }, [debouncedSearchValue]);

  useEffect(() => {
    if (isSearching) setFocusedIndex(null);
  }, [isSearching]);

  const onClearInput = () => {
    setFocus(false);
    setSearchValue('');
  };

  const onInputChange: ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement> = (e) => {
    if (e.target.value.length < 100) {
      setSearchValue(e.target.value);
    }
  };

  const handleKeyDown: KeyboardEventHandler<HTMLDivElement> = (e) => {
    if (e.key === 'Escape') {
      onClearInput();
      setFocusedIndex(null);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      setFocusedIndex(null);
      if (focusedIndex !== null) {
        resultsRef.current?.onTitleClick(results[focusedIndex as NonNullable<number>]);
      }
    } else if (e.key === 'ArrowDown' && results.length > 0) {
      setFocusedIndex((prev) => {
        if (prev === null || prev === results.length - 1) return 0;
        else return prev + 1;
      });
    } else if (e.key === 'ArrowUp' && results.length > 0) {
      setFocusedIndex((prev) => {
        if (prev === null || prev === 0) return results.length - 1;
        else return prev - 1;
      });
    }
  };

  const handleHistoryTitleClick = async (historicTitle: UserPreferences['titles']['history']) => {
    try {
      setTimeout(() => {
        if (isTitleV2) {
          dispatch(setActiveTitleId(null));
        } else {
          dispatch(setActiveTitle(null));
          dispatch(setFilteredChildId(null));
        }
        dispatch(clearInventoryResults(undefined));
        onClearInput();

        if (document.activeElement && document.activeElement instanceof HTMLElement) {
          document.activeElement.blur();
        }

        if (isTitleV2) {
          dispatch(clearCache());
          dispatch(setRootTitleId(historicTitle.id));
          dispatch(setActiveTitleId(historicTitle.id));
          dispatch(fetchTitleV2(historicTitle.id))
            .unwrap()
            .then((titleData) => {
              if (titleData.relationships.children.every((child) => child.entity_type === 'version')) {
                dispatch(getTitleVersions(titleData.id));
              }
            })
            .catch((err) => errorToast(err.message || 'There was a problem loading the title.'));
          dispatch(fetchTitleChildrenV2(historicTitle.id));
        } else {
          dispatch(setFilteredChildId(null));

          dispatch(getTitleDetails(historicTitle.id))
            .unwrap()
            .then(() => dispatch(setActiveTitle(historicTitle.id)))
            .catch((err) => errorToast(err.message || 'There was a problem loading the title.'));
        }
      }, 200);
    } catch (error) {
      console.error('Failed to load historic title:', error);
      errorToast(`Failed to load title "${historicTitle.name}". The title may no longer exist or you may not have permission to access it.`);
    }
  };

  const handleAddBulkIds = () => {
    dispatch(setToggledTitleSearchMode(TitleSearchMode.BULK_OR_PASTE));
    dispatch(setActiveTitleId(null));
    dispatch(setActiveTitle(null));
  }

  return (
    <CSSTransition in timeout={300} classNames="fade" unmountOnExit>
      <Grid container alignItems="center" sx={{ mb: 2, flexWrap: 'nowrap' }}>
        <TitleSearch xs={allowCsvOrPaste ? 10 : 12} item flexGrow={1}>
          <TextField
            ref={textFieldRef}
            id="title search"
            value={searchValue}
            onChange={onInputChange}
            onKeyDown={handleKeyDown}
            onFocus={() => setFocus(true)}
            onBlur={() => setFocus(false)}
            placeholder="Search by title name or ID"
            variant="filled"
            autoComplete="off"
            InputProps={{
              inputProps: { 'data-testid': 'title-search-input' },
              startAdornment: <PolarisIcon height={30} icon="search" style={{ color: 'var(--primary-main)' }} />,
              endAdornment: isSearching && <SearchSpinner size={26} />
            }}
            fullWidth
          />
          {focused && results.length === 0 && displayReadyTitleHistory.length > 0 ? (
            <TitleHistory>
              {displayReadyTitleHistory.map((title: UserPreferences['titles']['history']) => {
                return (
                  <TitleHistoryItem
                    key={title.id}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleHistoryTitleClick(title);
                    }}
                  >
                    <StyledHistoryIcon icon="history" />
                    <TitleHistoryItemText>{title.name}</TitleHistoryItemText>
                    <TitleHistoryItemSubtext>(ID: {title.id})</TitleHistoryItemSubtext>
                  </TitleHistoryItem>
                );
              })}
            </TitleHistory>
          ) : (
            <OPTitleSearchResults
              ref={resultsRef}
              show={showResults}
              isSearching={isSearching}
              clearInput={onClearInput}
              searchValue={searchValue}
              focusIndex={focusedIndex}
            />
          )}
        </TitleSearch>
        {allowCsvOrPaste && (
          <Grid item xs={2} sx={{ display: 'flex', justifyContent: 'flex-end', alignSelf: 'stretch', alignItems: 'center' }}>
            <Button onClick={handleAddBulkIds} startIcon={<PolarisIcon icon="add" height={18} />} variant="outlined" color="primary" sx={{ height: '100%' }}>
              Add Bulk IDs
            </Button>
          </Grid>
        )}
      </Grid>
    </CSSTransition>
  );
};

export default OPTitleSearch;

const TitleSearch = styled(Grid)`
  position: relative;
  width: 100%;
  z-index: 9;

  ${({ theme }) => convertStylesToStyled(theme.customOverrides?.TitleSearch)};

  & input {
    font-size: 1.2rem;
    padding: ${({ theme }) => theme.spacing(2)};
  }
`;

const TitleHistoryItem = styled.div`
  display: flex;
  font-size: 1.2rem;
  row-gap: ${({ theme }) => theme.spacing(2)};
  padding: ${({ theme }) => theme.spacing(1, 1.5)};

  &:hover {
    background-color: ${({ theme }) => theme.palette.action.hover};
    transition: 'background-color 0.3s ease-in-out';
    cursor: pointer;
  }
`;

const StyledHistoryIcon = styled(PolarisIcon)`
  height: 24px;
  width: 24px;
  color: ${({ theme }) => theme.palette.action.disabled};
  margin-right: ${({ theme }) => theme.spacing(0.5)};
`;

const TitleHistoryItemText = styled.span`
  margin-right: ${({ theme }) => theme.spacing(1)};
  font-style: italic;
`;

const TitleHistoryItemSubtext = styled.span`
  opacity: 0.5;
`;

const TitleHistory = styled.div`
  position: absolute;
  width: 100%;
  margin-top: ${({ theme }) => theme.spacing(1)};
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
  border: 1px solid ${({ theme }) => theme.palette.primary.main + '9a'};
  background: ${({ theme }) => theme.palette.background.paper};
  padding: ${({ theme }) => theme.spacing(1, 0)};
`;

const SearchSpinner = styled(Spinner)`
  right: ${({ theme }) => theme.spacing(2)};
  left: auto !important;
`;
